import { useEffect, useId, useState } from "react"

import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { Button } from "@/clientWeb/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Label } from "@/clientWeb/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/clientWeb/components/ui/radio-group"
import type { SeriesScope } from "../utils/recurrence"

interface SeriesScopeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Edit asks where to go next; delete is the confirmation itself. */
  mode: "edit" | "delete"
  onConfirm: (scope: SeriesScope) => void
  busy?: boolean
}

/**
 * "This occurrence" or "All occurrences", asked before editing or deleting a
 * day of a recurring meeting. "All" means every meeting of the series that has
 * not happened yet; past meetings are never touched.
 */
export function SeriesScopeDialog({ open, onOpenChange, mode, onConfirm, busy }: SeriesScopeDialogProps) {
  const { t } = useLanguage()
  const uid = useId()
  const [scope, setScope] = useState<SeriesScope>("one")
  // Each time it opens it starts on the safer choice.
  useEffect(() => {
    if (open) setScope("one")
  }, [open])

  const options: { value: SeriesScope; label: string }[] = [
    { value: "one", label: t("meetings.series.scopeThis") },
    { value: "all", label: t("meetings.series.scopeAll") },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xs">
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle id={`${uid}-title`}>
            {t(mode === "edit" ? "meetings.series.scopeEditTitle" : "meetings.series.scopeDeleteTitle")}
          </DialogTitle>
        </DialogHeader>
        <RadioGroup
          value={scope}
          onValueChange={(v) => setScope(v as SeriesScope)}
          aria-labelledby={`${uid}-title`}
          className="gap-2"
        >
          {options.map((o) => (
            <Label
              key={o.value}
              htmlFor={`${uid}-${o.value}`}
              className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer font-normal has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
            >
              <RadioGroupItem value={o.value} id={`${uid}-${o.value}`} />
              <span className="text-sm font-medium text-foreground">{o.label}</span>
            </Label>
          ))}
        </RadioGroup>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={mode === "delete" ? "destructive" : "default"}
            onClick={() => onConfirm(scope)}
            disabled={busy}
          >
            {mode === "delete" ? t("common.delete") : t("meetings.series.scopeContinue")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
